import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, Dropdown, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { bandTier } from 'components/upb_match_explorer/constants';
import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  INFO_BG,
  INFO_BORDER,
  INFO_COLOR,
  PRIMARY_BORDER,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA,
  RADIUS_LG,
  SHADOW_SM,
  WARNING_BORDER,
  WARNING_COLOR,
  WARNING_LIGHT,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';
import { downloadFile, httpGet } from 'lib/networking/http';
import { fmtInt, fmtPct, fmtUsd } from 'components/upb_match_explorer/format';
import FsaLoader from 'components/shared/FsaLoader';
import {
  getURLWithSearchParams,
  MANUFACTURER_SKU_MAPPING_MANUFACTURERS,
} from 'lib/networking/endpoints';
import InfluenceInfoTip from 'components/influence_sku_mapping/InfluenceInfoTip';
import InfluenceManufacturerProductsView from 'components/influence_sku_mapping/InfluenceManufacturerProductsView';
import InfluenceStatCard from 'components/influence_sku_mapping/InfluenceStatCard';
import { InfluenceStatusOptions } from 'lib/enums/InfluenceStatus';
import SwalDialog from 'lib/utils/toast';

const TAM_TIP =
  'TAM: influence-enabled SKUs on this distributor of the manufacturer’s campaign brands that ' +
  'are either mapped to a campaign MPN or not yet mapped to any MPN (available to map).';
const MAPPED_TIP = 'Mapped SKUs ÷ TAM SKUs on this distributor.';
const GMV_TIP = 'Annualized GMV of mapped SKUs ÷ annualized GMV of TAM SKUs on this distributor.';
const GMV_AMT_TIP = 'Annualized mapped-SKU GMV / all TAM-SKU GMV on this distributor.';

// A GMV % maps onto the same 0-25/25-50/50-75/75-100 bands as the overview pills.
const bandForPct = pct => (pct >= 75 ? 3 : pct >= 50 ? 2 : pct >= 25 ? 1 : 0);

// Human-readable labels for the ChangeLog link_type provenance values.
// The backend collapses raw ChangeLog link types into these three provenance groups.
const LINK_TYPE_LABELS = {
  AUTOMATED: 'Automated',
  EZ_PASS: 'EZ pass',
  BY_MANUFACTURER: 'By manufacturer',
};
const linkTypeLabel = key => LINK_TYPE_LABELS[key] || key;

// Per-group accent: colored dot + tinted pill + solid count badge.
const LINK_TYPE_ACCENTS = {
  AUTOMATED: { color: INFO_COLOR, bg: INFO_BG, border: INFO_BORDER },
  EZ_PASS: { color: PRIMARY_COLOR, bg: PRIMARY_LIGHT_RGBA, border: PRIMARY_BORDER },
  BY_MANUFACTURER: { color: WARNING_COLOR, bg: WARNING_LIGHT, border: WARNING_BORDER },
};
const linkTypeAccent = key => LINK_TYPE_ACCENTS[key] || LINK_TYPE_ACCENTS.AUTOMATED;

// The drill-in's three tabs: the DP-first rollup (default) plus the manufacturer's full
// mapped / unmapped product lists (rendered by the embedded products view).
const TABS = [
  { key: 'distributors', label: 'Distributors' },
  { key: 'mapped', label: 'Mapped' },
  { key: 'unmapped', label: 'Unmapped' },
];

/**
 * InfluenceManufacturerDistributorsView — the dashboard's DP-first drill-in: one row
 * per influence-enabled distributor carrying the manufacturer's branded SKUs, with the
 * TAM SKU count and the TAM-based mapped % and GMV %. Drilling into a distributor opens
 * that DP's product list.
 */
function InfluenceManufacturerDistributorsView() {
  const history = useHistory();
  const { manufacturerId } = useParams();
  const { search } = useLocation();
  // Inherited from the dashboard filter: scope the drill-in to this product campaign status.
  const campaignStatus = new URLSearchParams(search).get('campaign_product_status') || '';
  const statusParams = campaignStatus ? { campaign_product_status: campaignStatus } : {};

  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('distributors');
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    setError(false);
    httpGet(
      getURLWithSearchParams(
        `${MANUFACTURER_SKU_MAPPING_MANUFACTURERS}/${manufacturerId}/distributors`,
        campaignStatus ? { campaign_product_status: campaignStatus } : {},
      ),
    )
      .then(res => {
        if (seq === requestSeq.current) {
          setData(res.data);
        }
      })
      .catch(() => {
        if (seq === requestSeq.current) {
          setError(true);
        }
      })
      .finally(() => {
        if (seq === requestSeq.current) {
          setLoading(false);
        }
      });
  }, [manufacturerId, campaignStatus]);

  const distributors = data?.distributors || [];
  const gmvPct = data?.total_gmv ? (data.mapped_gmv / data.total_gmv) * 100 : 0;
  const openDistributor = distributorId =>
    history.push(
      getURLWithSearchParams(
        `/influence-sku-mapping/manufacturers/${manufacturerId}/dp/${distributorId}`,
        statusParams,
      ),
    );

  // Status filter mirrors the dashboard's; it lives in the URL so the fetch + export pick it up.
  const statusLabel =
    (InfluenceStatusOptions.find(o => o.value === campaignStatus) || {}).label || 'All statuses';
  const setStatus = value =>
    history.replace(
      getURLWithSearchParams(
        `/influence-sku-mapping/manufacturers/${manufacturerId}`,
        value ? { campaign_product_status: value } : {},
      ),
    );

  const [exporting, setExporting] = useState(false);
  const onExport = activeOnly => {
    setExporting(true);
    const base = `${MANUFACTURER_SKU_MAPPING_MANUFACTURERS}/${manufacturerId}/products/export`;
    const suffix = activeOnly ? '_active_only' : '';
    downloadFile(
      getURLWithSearchParams(base, {
        ...statusParams,
        ...(activeOnly ? { active_only: true } : {}),
      }),
      `vendor_products${suffix}.xlsx`,
    )
      .catch(() =>
        SwalDialog('error', 'Could not export vendor products. Please try again.', 'Export failed'),
      )
      .finally(() => setExporting(false));
  };

  // DP products with their mfr-mapping. Status-filter-independent (always the ACTIVE-catalog
  // brand set), so it sends no campaign_product_status.
  const onExportDpMfrLinks = () => {
    setExporting(true);
    downloadFile(
      `${MANUFACTURER_SKU_MAPPING_MANUFACTURERS}/${manufacturerId}/dp-mfr-link-export`,
      'dp_mfr_links.xlsx',
    )
      .catch(() =>
        SwalDialog('error', 'Could not export DP → mfr links. Please try again.', 'Export failed'),
      )
      .finally(() => setExporting(false));
  };

  // Initial load (single fetch): one full-view loader instead of a header + section loader.
  if (loading) {
    return (
      <div className={css(styles.loadingWrap)}>
        <FsaLoader fullView size={96} label="Loading distributors…" />
      </div>
    );
  }

  return (
    <div className={css(styles.page)}>
      <Button
        variant="link"
        type="button"
        className={css(styles.back)}
        onClick={() => history.push('/influence-sku-mapping')}
      >
        ← Influence SKU mapping
      </Button>
      <div className={css(styles.titleRow)}>
        <h1 className={css(styles.title)}>{data?.manufacturer_name || 'Manufacturer'}</h1>
        <div className={css(styles.headerActions)}>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              {`Status: ${statusLabel}`}
            </Dropdown.Toggle>
            <Dropdown.Menu align="right">
              <Dropdown.Item onClick={() => setStatus('')}>All statuses</Dropdown.Item>
              {InfluenceStatusOptions.map(o => (
                <Dropdown.Item key={o.value} onClick={() => setStatus(o.value)}>
                  {o.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" size="sm" disabled={exporting}>
              <FontAwesomeIcon icon={faDownload} className="mr-1" />
              {exporting ? 'Exporting…' : 'Export vendor products'}
            </Dropdown.Toggle>
            <Dropdown.Menu align="right">
              <Dropdown.Item onClick={() => onExport(false)}>Vendor products</Dropdown.Item>
              <Dropdown.Item onClick={() => onExport(true)}>
                Vendor products (active only)
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={onExportDpMfrLinks}>
                DP products with mfr-mapping
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <p className={css(styles.subtitle)}>
        Influence-enabled distributors carrying this manufacturer’s branded SKUs, highest GMV first.
        Open a distributor to review its products.
      </p>

      {!error && data && (
        <div className={css(styles.summary)}>
          <InfluenceStatCard
            accent="success"
            label="Mapped SKUs"
            value={fmtPct(data.mapped_pct)}
            caption="Campaign-brand DP SKUs mapped to a campaign MPN, of TAM (mapped + open)"
            ratio={data.mapped_pct}
            footer={`${fmtInt(data.total_mapped)} mapped / ${fmtInt(data.total_tam)} TAM SKUs`}
          />
          <InfluenceStatCard
            accent="info"
            label="Mapped GMV"
            value={fmtPct(gmvPct)}
            caption="Mapped GMV · of TAM GMV"
            ratio={gmvPct}
            footer={`${fmtUsd(data.mapped_gmv)} / ${fmtUsd(data.total_gmv)}`}
          />
          <InfluenceStatCard
            accent="warning"
            label="Influence DPs"
            value={fmtInt(data.count)}
            caption="Distinct influence distributors carrying them"
          />
          <InfluenceStatCard
            accent="info"
            label="Off-Brand SKUs"
            value={fmtInt(data.off_brand_skus)}
            caption="Mapped to a campaign MPN but off a campaign brand (outside TAM)"
            footer={`${fmtUsd(data.off_brand_gmv)} GMV`}
          />
          <InfluenceStatCard
            accent="primary"
            label="EZ Pass Mapped"
            value={fmtInt(data.ez_pass_skus)}
            caption="Campaign-MPN-mapped DP SKUs mapped via the Brand SKU Mapper (EZ-pass) tools"
            footer={`${fmtUsd(data.ez_pass_gmv)} GMV`}
          />
          {/* Status-only reconciliation cards -- mirror the overview page, scoped to this
              manufacturer (the backend returns these fields only when a status is selected). */}
          {campaignStatus && data.mpn_sku_influence_skus != null && (
            <>
              <InfluenceStatCard
                accent="primary"
                label="Avg MPN-SKU Maps"
                value={Number(data.avg_mpn_sku_maps || 0).toFixed(1)}
                caption="Avg influence DP SKUs mapped per campaign MPN"
                footer={`${fmtInt(data.mpn_sku_influence_skus)} SKUs / ${fmtInt(
                  data.campaign_skus,
                )} MPNs`}
              />
              <InfluenceStatCard
                accent="success"
                label="MPN-SKU Influence SKUs"
                value={fmtInt(data.mpn_sku_influence_skus)}
                caption="Influence DP SKUs mapped to a campaign MPN (incl. off-brand matches)"
                footer={`${fmtUsd(data.mpn_sku_influence_gmv)} GMV`}
              />
              <InfluenceStatCard
                accent="warning"
                label="Outside Campaign SKUs"
                value={fmtInt(data.outside_campaign_skus)}
                caption="Influence DP SKUs mapped to active, non-campaign catalog products"
                footer={`${fmtUsd(data.outside_campaign_gmv)} GMV`}
              />
            </>
          )}
        </div>
      )}

      {!error && (data?.link_types || []).length > 0 && (
        <div className={css(styles.linkSection)}>
          <div className={css(styles.linkTitle)}>Mapped SKUs by link type</div>
          <div className={css(styles.linkChips)}>
            {data.link_types.map(lt => {
              const accent = linkTypeAccent(lt.link_type);
              return (
                <span
                  key={lt.link_type}
                  className={css(styles.linkChip)}
                  style={{ background: accent.bg, borderColor: accent.border }}
                >
                  <span className={css(styles.linkDot)} style={{ background: accent.color }} />
                  <span className={css(styles.linkChipLabel)}>{linkTypeLabel(lt.link_type)}</span>
                  <span className={css(styles.linkChipCount)} style={{ background: accent.color }}>
                    {fmtInt(lt.count)}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className={css(styles.tabBar)} role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={css(styles.tab, tab === t.key && styles.tabActive)}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'mapped' && <InfluenceManufacturerProductsView embedded fixedMapped="mapped" />}
      {tab === 'unmapped' && <InfluenceManufacturerProductsView embedded fixedMapped="unmapped" />}

      {tab === 'distributors' &&
        (error ? (
          <Alert variant="danger">Failed to load distributors. Please try again.</Alert>
        ) : (
          <>
            <div className={css(styles.tableMeta)}>
              {fmtInt(distributors.length)}{' '}
              {distributors.length === 1 ? 'distributor' : 'distributors'}
            </div>
            <div className={css(styles.tableWrap)}>
              <Table hover className={css(styles.table)}>
                <thead>
                  <tr>
                    <th>Distributor</th>
                    <th className={css(styles.numCol)}>
                      Mapped / TAM
                      <InfluenceInfoTip id="dp-tip-tam" text={TAM_TIP} />
                    </th>
                    <th className={css(styles.numCol)}>
                      Mapped %<InfluenceInfoTip id="dp-tip-mapped" text={MAPPED_TIP} />
                    </th>
                    <th className={css(styles.numCol)}>
                      GMV %<InfluenceInfoTip id="dp-tip-gmv" text={GMV_TIP} />
                    </th>
                    <th className={css(styles.numCol)}>
                      Mapped / TAM GMV
                      <InfluenceInfoTip id="dp-tip-gmv-amt" text={GMV_AMT_TIP} />
                    </th>
                    <th className={css(styles.actionCol)} />
                  </tr>
                </thead>
                <tbody>
                  {distributors.length === 0 ? (
                    <tr>
                      <td colSpan="6" className={css(styles.empty)}>
                        No influence distributors carry this manufacturer’s branded SKUs yet.
                      </td>
                    </tr>
                  ) : (
                    distributors.map(dp => {
                      const tier = bandTier(bandForPct(dp.gmv_pct));
                      return (
                        <tr key={dp.distributor_id}>
                          <td className={css(styles.dpName)}>{dp.distributor_name || '—'}</td>
                          <td className={css(styles.numCol)}>
                            <span className={css(styles.mappedFrac)}>{fmtInt(dp.mapped)}</span>
                            <span className={css(styles.tamDenom)}> / {fmtInt(dp.tam)}</span>
                          </td>
                          <td className={css(styles.numCol)}>{fmtPct(dp.mapped_pct)}</td>
                          <td className={css(styles.numCol)}>
                            <span
                              className={css(styles.pctPill)}
                              style={{ backgroundColor: tier.bg, color: tier.text }}
                            >
                              {fmtPct(dp.gmv_pct)}
                            </span>
                          </td>
                          <td className={css(styles.numCol)}>
                            <span className={css(styles.mappedFrac)}>{fmtUsd(dp.mapped_gmv)}</span>
                            <span className={css(styles.tamDenom)}> / {fmtUsd(dp.tam_gmv)}</span>
                          </td>
                          <td className={css(styles.actionCol)}>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => openDistributor(dp.distributor_id)}
                            >
                              View →
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </>
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  page: { background: BG_2, minHeight: '100%', padding: '28px 32px 48px' },
  back: {
    display: 'inline-block',
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 13,
    fontWeight: 600,
    color: PRIMARY_COLOR,
    cursor: 'pointer',
    marginBottom: 12,
    textDecoration: 'none',
    ':hover': { color: PRIMARY_COLOR, textDecoration: 'none' },
    ':focus': { textDecoration: 'none', boxShadow: 'none' },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 700, color: FG_1, margin: '0 0 4px' },
  subtitle: { fontSize: 14, color: FG_2, marginBottom: 20, maxWidth: 760 },
  tabBar: {
    display: 'flex',
    gap: 4,
    borderBottom: `1px solid ${BORDER_2}`,
    margin: '4px 0 20px',
  },
  tab: {
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '8px 14px',
    marginBottom: -1,
    fontSize: 14,
    fontWeight: 600,
    color: FG_2,
    cursor: 'pointer',
    ':hover': { color: FG_1 },
    ':focus': { outline: 'none', boxShadow: 'none' },
  },
  tabActive: {
    color: PRIMARY_COLOR,
    borderBottomColor: PRIMARY_COLOR,
  },
  tableMeta: { fontSize: 13, color: FG_3, marginBottom: 10, fontWeight: 600 },
  // Fixed 4-column grid (max 4 per row) like the overview page; collapses to 2 when narrow.
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 16,
    marginBottom: 22,
    '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  linkSection: { marginBottom: 22 },
  linkTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: FG_3,
    marginBottom: 10,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  linkChips: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  linkChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid transparent',
    borderRadius: 999,
    padding: '5px 6px 5px 12px',
  },
  linkDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  linkChipLabel: { fontSize: 13, fontWeight: 600, color: FG_1 },
  linkChipCount: {
    fontSize: 12,
    fontWeight: 700,
    color: WHITE,
    fontVariantNumeric: 'tabular-nums',
    borderRadius: 999,
    padding: '2px 9px',
    minWidth: 24,
    textAlign: 'center',
  },
  loadingWrap: { background: BG_2 },
  tableWrap: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    overflowX: 'auto',
  },
  table: { fontSize: 13, color: FG_2, marginBottom: 0, verticalAlign: 'middle' },
  numCol: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  actionCol: { textAlign: 'right', whiteSpace: 'nowrap' },
  dpName: { fontWeight: 600, color: FG_1 },
  mappedFrac: { fontWeight: 600, color: FG_1 },
  tamDenom: { color: FG_3 },
  pctPill: {
    display: 'inline-block',
    minWidth: 58,
    textAlign: 'center',
    fontWeight: 600,
    fontSize: 12.5,
    padding: '3px 8px',
    borderRadius: 20,
  },
  empty: { textAlign: 'center', color: FG_3, padding: '28px 8px' },
});

export default InfluenceManufacturerDistributorsView;
